/**
 * The AI-suggested title of a multi-member alert (task 77b).
 *
 * Sits under the deterministic title in the alert header, visibly
 * labeled. The deterministic title stays the record — it is what rules
 * and audit see; this one is the incident's story for a human reading the
 * queue, and it is never matchable (ADR-0011).
 *
 *   none     → a role that may triage asks once; viewer sees nothing
 *   pending  → "Drafting a title…", polling the detail, bounded
 *   ready    → the chip + the title, model and age in the tooltip
 *   stale    → the old title, "based on N of M detections", re-request once
 *   failed   → one muted line and Try again for triage roles
 *
 * Renders nothing at all for a single-member alert.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, requestAlertTitle, type AiTitle as AiTitleData } from '@bluesphere/api-client';

import { useMdrToken } from './context.js';
import { timeAgo } from './format.js';
import { useBoundedPoll } from './useBoundedPoll.js';

const ADVISORY = 'AI-generated, advisory, never decides. Not matchable in rules.';

export function AiTitle({
  tenantId,
  alertRef,
  aiTitle,
  canRequest,
  refreshKey,
}: {
  tenantId: string;
  alertRef: string;
  aiTitle: AiTitleData;
  /** Whether the signed-in role may ask for a title (not viewer). */
  canRequest: boolean;
  /** The alert detail's query key; re-fetched while pending. */
  refreshKey: unknown[];
}) {
  const idToken = useMdrToken();
  const queryClient = useQueryClient();
  const [requestError, setRequestError] = useState<unknown>(null);
  const [localPending, setLocalPending] = useState(false);
  const requestedFor = useRef<number | null>(null);

  const multi = aiTitle.member_count >= 2;
  const wants = aiTitle.state === 'none' || aiTitle.state === 'stale';
  const state = localPending && wants ? 'pending' : aiTitle.state;

  const ask = () => {
    setRequestError(null);
    setLocalPending(true);
    requestAlertTitle(tenantId, alertRef, idToken)
      .then(() => void queryClient.invalidateQueries({ queryKey: refreshKey }))
      .catch((error: unknown) => {
        setLocalPending(false);
        setRequestError(error);
      });
  };

  // First open, or the member set grew: ask once per member count.
  useEffect(() => {
    if (!multi || !wants || !canRequest || !idToken) return;
    if (requestedFor.current === aiTitle.member_count) return;
    requestedFor.current = aiTitle.member_count;
    ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, wants, canRequest, idToken, aiTitle.member_count]);

  useEffect(() => {
    if (aiTitle.state === 'ready' || aiTitle.state === 'failed') setLocalPending(false);
  }, [aiTitle.state]);

  useBoundedPoll(state === 'pending', refreshKey);

  if (!multi) return null;

  if ((state === 'ready' || state === 'stale') && aiTitle.title) {
    return (
      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-bluesphere-textSecondary">
        <Chip />
        <span
          className="italic"
          title={`${ADVISORY} Generated by ${aiTitle.model ?? 'Claude'}${
            aiTitle.generated_at ? ` ${timeAgo(aiTitle.generated_at)}` : ''
          }.`}
        >
          “{aiTitle.title}”
        </span>
        {state === 'stale' && (
          <span className="text-xs text-bluesphere-muted">
            based on {aiTitle.member_count_at} of {aiTitle.member_count} detections
            {localPending ? ' · updating…' : ''}
          </span>
        )}
      </p>
    );
  }

  if (state === 'pending') {
    return (
      <p className="mt-1 flex items-center gap-2 text-sm text-bluesphere-muted" role="status">
        <Chip />
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-bluesphere-primary"
        />
        Drafting a title for the alert as a whole…
      </p>
    );
  }

  if (state === 'failed') {
    return (
      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-bluesphere-muted">
        <Chip />
        No AI title{aiTitle.failed_reason ? ` (${aiTitle.failed_reason})` : ''}.
        {canRequest && (
          <button type="button" onClick={ask} className="underline hover:text-bluesphere-primary">
            Try again
          </button>
        )}
        {requestError != null && <RequestError error={requestError} />}
      </p>
    );
  }

  // none, and this role cannot ask (or the ask failed)
  if (requestError != null) {
    return (
      <p className="mt-1 text-xs text-bluesphere-muted">
        <RequestError error={requestError} />
      </p>
    );
  }
  return null;
}

function Chip() {
  return (
    <span
      className="rounded-full border border-bluesphere-primary/30 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-bluesphere-primary"
      title={ADVISORY}
    >
      AI-suggested
    </span>
  );
}

function RequestError({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : String(error);
  return <span className="text-red-700">Could not request a title. {message}</span>;
}
